<script lang="ts">
  import { type Warehouse, type Business } from '../../lib/types';

  let {
    isOpen,
    warehouse = null,
    businesses,
    linkedBusinessIds,
    primaryBusinessId,
    isLoading,
    onlink,
    onunlink,
    onclose,
  }: {
    isOpen: boolean;
    warehouse?: Warehouse | null;
    businesses: Business[];
    linkedBusinessIds: string[];
    primaryBusinessId: string;
    isLoading: boolean;
    onlink: (businessId: string) => void;
    onunlink: (businessId: string) => void;
    onclose: () => void;
  } = $props();

  function isLinked(businessId: string): boolean {
    return linkedBusinessIds.includes(businessId);
  }

  function isPrimary(businessId: string): boolean {
    return businessId === primaryBusinessId;
  }

  function handleToggle(businessId: string) {
    if (isPrimary(businessId)) return;
    if (isLinked(businessId)) {
      onunlink(businessId);
    } else {
      onlink(businessId);
    }
  }
</script>

{#if isOpen && warehouse}
  <div class="modal-overlay" onclick={onclose}>
    <div class="modal link-modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>Link Warehouse to Businesses</h3>
        <button class="btn-close" onclick={onclose}>&times;</button>
      </div>

      <div class="warehouse-summary">
        <div class="warehouse-summary-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 21h18M3 10h18M3 10l9-7 9 7M4 10v11m16-11v11M8 14v3m4-3v3m4-3v3" />
          </svg>
        </div>
        <div>
          <span class="warehouse-summary-name">{warehouse.name}</span>
          {#if warehouse.address}
            <span class="warehouse-summary-address">{warehouse.address}</span>
          {/if}
        </div>
      </div>

      {#if isLoading}
        <div class="link-loading">
          <div class="spinner"></div>
          <p>Loading linked businesses...</p>
        </div>
      {:else}
        <div class="business-link-list">
          <p class="link-description">Toggle businesses to share this warehouse across multiple businesses. The owning business cannot be unlinked.</p>

          {#each businesses as business}
            {@const linked = isLinked(business.id)}
            {@const primary = isPrimary(business.id)}
            <div class="business-link-item" class:linked class:primary>
              <div class="business-link-info">
                <span class="business-link-name">{business.name}</span>
                {#if primary}
                  <span class="owner-badge">Owner</span>
                {/if}
              </div>
              <button
                class="btn-toggle-link"
                class:active={linked}
                disabled={primary}
                onclick={() => handleToggle(business.id)}
                title={primary ? 'Cannot unlink the owning business' : linked ? 'Click to unlink' : 'Click to link'}
              >
                <div class="toggle-track">
                  <div class="toggle-thumb"></div>
                </div>
              </button>
            </div>
          {/each}
        </div>
      {/if}

      <div class="modal-actions">
        <button type="button" class="btn-secondary" onclick={onclose}>
          Done
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .link-modal {
    max-width: 480px;
  }

  .warehouse-summary {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: var(--color-gray-50, #f9fafb);
    border: 1px solid var(--color-gray-200, #e5e7eb);
    border-radius: 8px;
    margin-bottom: 1.25rem;
  }

  .warehouse-summary-icon {
    width: 40px;
    height: 40px;
    background: var(--color-gray-200, #e5e7eb);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .warehouse-summary-icon svg {
    width: 20px;
    height: 20px;
    color: var(--color-gray-500, #6b7280);
  }

  .warehouse-summary-name {
    display: block;
    font-weight: 600;
    color: var(--color-gray-800, #1f2937);
  }

  .warehouse-summary-address {
    display: block;
    font-size: 0.85rem;
    color: var(--color-gray-500, #6b7280);
  }

  .link-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
    color: var(--color-gray-500, #6b7280);
  }

  .link-loading .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--color-gray-200, #e5e7eb);
    border-top-color: var(--color-primary, #667eea);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 0.75rem;
  }

  .link-description {
    font-size: 0.85rem;
    color: var(--color-gray-500, #6b7280);
    margin: 0 0 1rem;
    line-height: 1.4;
  }

  .business-link-list {
    margin-bottom: 1rem;
  }

  .business-link-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border: 1px solid var(--color-gray-200, #e5e7eb);
    border-radius: 6px;
    margin-bottom: 0.5rem;
    background: white;
    transition: all 0.2s;
  }

  .business-link-item.linked {
    border-color: #a5b4fc;
    background: #f5f7ff;
  }

  .business-link-item.primary {
    border-color: #a5b4fc;
    background: linear-gradient(135deg, #f5f7ff 0%, #ede9fe 100%);
  }

  .business-link-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .business-link-name {
    font-weight: 500;
    color: var(--color-gray-800, #1f2937);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .owner-badge {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }

  /* Toggle switch */
  .btn-toggle-link {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    flex-shrink: 0;
  }

  .btn-toggle-link:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .toggle-track {
    width: 44px;
    height: 24px;
    border-radius: 12px;
    background: var(--color-gray-300, #d1d5db);
    position: relative;
    transition: background-color 0.2s;
  }

  .btn-toggle-link.active .toggle-track {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .btn-toggle-link:disabled .toggle-track {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  .toggle-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    position: absolute;
    top: 2px;
    left: 2px;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  .btn-toggle-link.active .toggle-thumb {
    transform: translateX(20px);
  }

  .btn-toggle-link:disabled .toggle-thumb {
    transform: translateX(20px);
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>
